import { Controller } from "@hotwired/stimulus"
import * as THREE from "three"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js"

// Cache for loaded models
const modelCache = new Map()

// Inspirational quotes from Napoleon Hill and New Thought authors
const WISDOM_QUOTES = [
  { text: "Whatever the mind can conceive and believe, it can achieve.", author: "Napoleon Hill" },
  { text: "The starting point of all achievement is desire.", author: "Napoleon Hill" },
  { text: "Strength and growth come only through continuous effort.", author: "Napoleon Hill" },
  { text: "Don't wait. The time will never be just right.", author: "Napoleon Hill" },
  { text: "A goal is a dream with a deadline.", author: "Napoleon Hill" },
  { text: "Every adversity carries the seed of equal or greater benefit.", author: "Napoleon Hill" },
  { text: "You are the master of your destiny.", author: "Napoleon Hill" },
  { text: "Action is the real measure of intelligence.", author: "Napoleon Hill" },
  { text: "Patience, persistence and perspiration make an unbeatable combination.", author: "Napoleon Hill" },
  { text: "Change your thoughts and you change your world.", author: "Norman Vincent Peale" },
  { text: "Believe in yourself! Have faith in your abilities!", author: "Norman Vincent Peale" },
  { text: "The only limit to our realization of tomorrow is our doubts of today.", author: "Roosevelt" },
  { text: "What we think, we become.", author: "Buddha" },
  { text: "The universe is change; our life is what our thoughts make it.", author: "Marcus Aurelius" },
  { text: "As a man thinketh in his heart, so is he.", author: "James Allen" },
  { text: "Dream lofty dreams, and as you dream, so shall you become.", author: "James Allen" },
  { text: "The soul attracts that which it secretly harbors.", author: "James Allen" },
  { text: "Circumstances do not make the man, they reveal him.", author: "James Allen" },
  { text: "Imagination is the preview of life's coming attractions.", author: "Einstein" },
  { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { text: "Great things never come from comfort zones.", author: "Unknown" },
  { text: "Thoughts become things. Choose the good ones.", author: "Mike Dooley" },
  { text: "What you seek is seeking you.", author: "Rumi" },
  { text: "Be the change you wish to see in the world.", author: "Gandhi" },
  { text: "The best time to plant a tree was 20 years ago. The second best is now.", author: "Proverb" },
  { text: "You miss 100% of the shots you don't take.", author: "Wayne Gretzky" },
  { text: "Whether you think you can or can't, you're right.", author: "Henry Ford" },
  { text: "The future belongs to those who believe in the beauty of their dreams.", author: "Eleanor Roosevelt" },
  { text: "It is never too late to be what you might have been.", author: "George Eliot" },
  { text: "The only impossible journey is the one you never begin.", author: "Tony Robbins" },
  { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
  { text: "Your time is limited. Don't waste it living someone else's life.", author: "Steve Jobs" },
  { text: "In the middle of difficulty lies opportunity.", author: "Einstein" },
  { text: "Success is not final, failure is not fatal: it is the courage to continue.", author: "Churchill" },
  { text: "The way to get started is to quit talking and begin doing.", author: "Walt Disney" },
  { text: "Everything you've ever wanted is on the other side of fear.", author: "George Addair" },
  { text: "You are never too old to set another goal or dream a new dream.", author: "C.S. Lewis" },
  { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { text: "Life is 10% what happens to us and 90% how we react to it.", author: "Charles Swindoll" },
  { text: "Act as if what you do makes a difference. It does.", author: "William James" },
  { text: "What lies behind us and before us are tiny matters compared to what lies within us.", author: "Emerson" },
  { text: "Happiness is not something ready made. It comes from your own actions.", author: "Dalai Lama" },
  { text: "The mind is its own place, and can make a heaven of hell.", author: "John Milton" },
  { text: "Do what you can, with what you have, where you are.", author: "Theodore Roosevelt" },
  { text: "We become what we think about most of the time.", author: "Earl Nightingale" },
  { text: "Your thoughts create your reality.", author: "Unknown" },
  { text: "Every moment is a fresh beginning.", author: "T.S. Eliot" },
  { text: "The energy you bring is the energy you attract.", author: "Unknown" },
  { text: "Progress, not perfection.", author: "Unknown" },
  { text: "Today I choose joy.", author: "Unknown" },
  { text: "I am worthy of all good things.", author: "Unknown" },
  { text: "My potential is limitless.", author: "Unknown" },
  { text: "I trust the journey.", author: "Unknown" },
  { text: "Every day is a new opportunity.", author: "Unknown" },
  { text: "I am becoming the best version of myself.", author: "Unknown" },
  { text: "Gratitude turns what we have into enough.", author: "Unknown" },
  { text: "I radiate positivity and attract positivity.", author: "Unknown" },
  { text: "My mind is clear, focused, and sharp.", author: "Unknown" },
  { text: "I embrace change and welcome growth.", author: "Unknown" },
  { text: "The best is yet to come.", author: "Unknown" }
]

export default class extends Controller {
  static targets = ["canvas", "loader", "thoughtBubble"]
  static values = {
    modelPath: { type: String, default: "/mascot.glb" },
    followMouse: { type: Boolean, default: true }
  }

  connect() {
    if (!this.hasCanvasTarget) return

    // Skip 3D rendering on mobile devices to save resources
    if (this.isMobileDevice()) {
      this.hideLoader()
      this.hideCanvas()
      return
    }

    if (!this.isWebGLAvailable()) {
      this.hideLoader()
      return
    }

    this.setupIntersectionObserver()
  }

  isMobileDevice() {
    // Check for touch capability and small viewport
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const isSmallScreen = window.innerWidth < 1024 // lg breakpoint in Tailwind
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    return (isTouchDevice && isSmallScreen) || isMobileUA
  }

  hideCanvas() {
    if (this.hasCanvasTarget) {
      this.canvasTarget.style.display = 'none'
    }
    // Hide the entire container
    this.element.style.display = 'none'
  }

  showLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.remove("hidden")
    }
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add("hidden")
    }
  }

  disconnect() {
    this.cleanup()
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.initialized) {
          this.initScene()
          this.observer.disconnect()
        }
      })
    }, { threshold: 0.1 })

    this.observer.observe(this.element)
  }

  isWebGLAvailable() {
    try {
      const canvas = document.createElement("canvas")
      return !!(window.WebGLRenderingContext &&
        (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")))
    } catch (e) {
      return false
    }
  }

  initScene() {
    if (this.initialized) return
    this.initialized = true

    const canvas = this.canvasTarget
    const container = canvas.parentElement
    const width = container.clientWidth
    const height = container.clientHeight || 280

    // Scene
    this.scene = new THREE.Scene()
    this.clock = new THREE.Clock()
    this.time = 0

    // Mouse tracking
    this.mouse = { x: 0, y: 0 }
    this.targetRotation = { x: 0, y: 0 }
    this.currentRotation = { x: 0, y: 0 }

    // Camera
    this.camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 1000)
    this.camera.position.set(0, 0.5, 4)

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true
    })
    this.renderer.setSize(width, height)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    this.renderer.setClearColor(0x000000, 0) // Fully transparent background
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.2

    this.addLights()
    this.loadModel()
    this.setupMouseTracking()
    this.animate()
    this.setupResizeObserver()
  }

  addLights() {
    // Warm brown ambient light
    const ambient = new THREE.AmbientLight(0x8b7355, 0.6)
    this.scene.add(ambient)

    // Main warm key light (top-right)
    const keyLight = new THREE.DirectionalLight(0xffeedd, 1.0)
    keyLight.position.set(3, 4, 5)
    keyLight.castShadow = false
    this.scene.add(keyLight)

    // Warm fill light from left
    const fillLight = new THREE.DirectionalLight(0xd4a574, 0.5)
    fillLight.position.set(-3, 2, 3)
    this.scene.add(fillLight)

    // Subtle rim light from behind (brown tint)
    const rimLight = new THREE.DirectionalLight(0x8b6914, 0.3)
    rimLight.position.set(0, 2, -4)
    this.scene.add(rimLight)

    // Warm bottom fill
    const bottomLight = new THREE.DirectionalLight(0xc4956a, 0.25)
    bottomLight.position.set(0, -3, 2)
    this.scene.add(bottomLight)

    // Animated accent light for subtle shimmer
    this.accentLight = new THREE.PointLight(0xdaa520, 0.4, 10)
    this.accentLight.position.set(2, 1, 3)
    this.scene.add(this.accentLight)
  }

  loadModel() {
    const modelPath = this.modelPathValue

    // Check cache first
    if (modelCache.has(modelPath)) {
      this.setupModelFromCache(modelCache.get(modelPath))
      return
    }

    this.showLoader()
    const loader = new GLTFLoader()

    loader.load(
      modelPath,
      (gltf) => {
        // Cache the loaded scene for reuse
        modelCache.set(modelPath, gltf)
        this.setupModelFromCache(gltf)
        this.hideLoader()
      },
      (progress) => {
        // Loading progress - could update a progress bar here
        if (progress.total > 0) {
          const percent = Math.round((progress.loaded / progress.total) * 100)
          this.updateLoadingProgress(percent)
        }
      },
      (error) => {
        console.warn("Failed to load mascot model:", error)
        this.hideLoader()
      }
    )
  }

  updateLoadingProgress(percent) {
    if (this.hasLoaderTarget) {
      const progressEl = this.loaderTarget.querySelector('[data-progress]')
      if (progressEl) {
        progressEl.textContent = `${percent}%`
      }
    }
  }

  setupModelFromCache(gltf) {
    // Clone the scene so each instance is independent
    this.model = gltf.scene.clone()

    // Center and scale the model
    const box = new THREE.Box3().setFromObject(this.model)
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())

    // Center the model and move up slightly so it's not cut off
    this.model.position.x = -center.x
    this.model.position.y = -center.y + 0.3
    this.model.position.z = -center.z

    // Scale to fit nicely
    const maxDim = Math.max(size.x, size.y, size.z)
    const scale = 2.0 / maxDim
    this.model.scale.setScalar(scale)

    // Adjust materials for warm brown lighting
    this.model.traverse((child) => {
      if (child.isMesh) {
        // Clone material so modifications don't affect cached version
        if (child.material) {
          child.material = child.material.clone()
          child.material.emissive = new THREE.Color(0x1a0f05)
          child.material.emissiveIntensity = 0.1
        }
      }
    })

    this.scene.add(this.model)
    this.hideLoader()
  }

  setupMouseTracking() {
    // Track mouse position across the entire document
    this.boundMouseMove = this.handleMouseMove.bind(this)
    document.addEventListener('mousemove', this.boundMouseMove)

    // Setup hover events for thought bubble
    this.setupThoughtBubble()
  }

  setupThoughtBubble() {
    // Create thought bubble element if it doesn't exist
    if (!this.hasThoughtBubbleTarget) {
      this.thoughtBubbleEl = document.createElement('div')
      this.thoughtBubbleEl.className = 'thought-bubble'
      this.thoughtBubbleEl.innerHTML = `
        <div class="thought-bubble-content">
          <p class="thought-quote"></p>
          <span class="thought-author"></span>
        </div>
        <div class="thought-bubble-tail"></div>
      `
      // Append to the canvas parent (the relative container)
      this.canvasTarget.parentElement.appendChild(this.thoughtBubbleEl)
    } else {
      this.thoughtBubbleEl = this.thoughtBubbleTarget
    }

    // Add hover listeners to canvas
    this.canvasTarget.addEventListener('mouseenter', () => this.showThoughtBubble())
    this.canvasTarget.addEventListener('mouseleave', () => this.hideThoughtBubble())
  }

  showThoughtBubble() {
    // Pick a random quote
    const quote = WISDOM_QUOTES[Math.floor(Math.random() * WISDOM_QUOTES.length)]

    // Update content
    const quoteEl = this.thoughtBubbleEl.querySelector('.thought-quote')
    const authorEl = this.thoughtBubbleEl.querySelector('.thought-author')

    if (quoteEl) quoteEl.textContent = quote.text
    if (authorEl) authorEl.textContent = `— ${quote.author}`

    // Show with animation
    this.thoughtBubbleEl.classList.add('visible')
  }

  hideThoughtBubble() {
    this.thoughtBubbleEl.classList.remove('visible')
  }

  handleMouseMove(event) {
    // Get canvas bounds
    const rect = this.canvasTarget.getBoundingClientRect()
    const canvasCenterX = rect.left + rect.width / 2
    const canvasCenterY = rect.top + rect.height / 2

    // Calculate mouse position relative to canvas center, normalized to -1 to 1
    // But allow the whole screen range
    const maxRange = Math.max(window.innerWidth, window.innerHeight) / 2
    this.mouse.x = (event.clientX - canvasCenterX) / maxRange
    this.mouse.y = (event.clientY - canvasCenterY) / maxRange

    // Calculate target rotation (limit the angle so it looks natural)
    const maxRotationY = Math.PI / 4  // 45 degrees left/right
    const maxRotationX = Math.PI / 6  // 30 degrees up/down

    this.targetRotation.y = this.mouse.x * maxRotationY
    this.targetRotation.x = this.mouse.y * maxRotationX
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize()
    })
    this.resizeObserver.observe(this.canvasTarget.parentElement)
  }

  handleResize() {
    if (!this.renderer || !this.camera) return

    const container = this.canvasTarget.parentElement
    const width = container.clientWidth
    const height = container.clientHeight || 280

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)
  }

  animate() {
    if (!this.renderer) return

    this.animationId = requestAnimationFrame(() => this.animate())

    const delta = this.clock.getDelta()
    this.time += delta

    // Subtle light animation
    if (this.accentLight) {
      this.accentLight.position.x = Math.sin(this.time * 0.3) * 2
      this.accentLight.position.y = 1 + Math.cos(this.time * 0.4) * 0.5
      this.accentLight.intensity = 0.3 + Math.sin(this.time * 0.5) * 0.1
    }

    // Smooth rotation towards mouse position
    if (this.model && this.followMouseValue) {
      const lerpFactor = 0.08 // Smoothing factor (lower = smoother)
      this.currentRotation.x += (this.targetRotation.x - this.currentRotation.x) * lerpFactor
      this.currentRotation.y += (this.targetRotation.y - this.currentRotation.y) * lerpFactor

      this.model.rotation.x = this.currentRotation.x
      this.model.rotation.y = this.currentRotation.y
    }

    this.renderer.render(this.scene, this.camera)
  }

  cleanup() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }

    if (this.observer) {
      this.observer.disconnect()
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove)
    }

    // Remove thought bubble if we created it
    if (this.thoughtBubbleEl && this.thoughtBubbleEl.parentNode) {
      this.thoughtBubbleEl.parentNode.removeChild(this.thoughtBubbleEl)
    }

    if (this.renderer) {
      this.renderer.dispose()
    }

    if (this.scene) {
      this.scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose()
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose())
          } else {
            object.material.dispose()
          }
        }
      })
    }
  }
}
